import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { QueryKey } from '@/Config/api/queryKey';
import { AppActionDto } from '@/Domain/api/Action/dto/Action.dto';
import { HandoffDto } from '@/Domain/api/Action/dto/Handoff.dto';
import { ActionBox } from '@/Domain/components/Action/ActionBox/ActionBox';
import { HandoffActionSheet } from '@/Domain/components/Action/HandoffAction/HandoffAction.sheet';
import { AppActionTypeEnum } from '@/Domain/enum/ActionType.enum';
import { MODAL_NAMES, useModalStore } from '@/Domain/store/modal.store';

export const HandoffActionBox = () => {
  const { t } = useTranslation('handoff_action');
  const queryClient = useQueryClient();

  const { toggleModal } = useModalStore();

  const { data, isFetched, dataUpdatedAt } = useQuery<HandoffDto>({
    queryKey: [QueryKey.handoff.get],
  });

  const mutation = useMutation<HandoffDto, unknown, HandoffDto>({
    mutationKey: [QueryKey.handoff.put],
    onSuccess: (handoff: HandoffDto) => {
      queryClient.setQueryData([QueryKey.handoff.get], handoff);

      if (handoff.settings.is_active) {
        toast.success(t('toast.enable_handoff'));
      } else {
        toast.success(t('toast.disable_handoff'));
      }
    },
  });

  const action: AppActionDto = useMemo(
    () => ({
      icon: 'FaUserCheck',
      icon_color: 'red',
      type: AppActionTypeEnum.Handoff,
      name: t('box.title'),
      description: t('box.description'),
      is_valid: true,
      is_active: data?.settings.is_active || false,
    }),
    [isFetched, dataUpdatedAt, t],
  );

  const handleActive = (isActive: boolean) =>
    mutation.mutate({ ...data!, settings: { ...data!.settings, is_active: isActive } });

  const handleEdit = () => toggleModal(MODAL_NAMES.HANDOFF_ACTION_SHEET);

  if (!data) {
    return null;
  }

  return (
    <>
      <ActionBox
        key={action.id}
        dataTestId="handoff-action"
        action={action}
        onActive={handleActive}
        onEdit={handleEdit}
        buttonLabel={t('button.edit')}
      />

      <HandoffActionSheet handoff={data} />
    </>
  );
};
